import { AddPrimitivesCommand, BasicControl, CURSOR_TYPE, DYMODE_MODE, Listeners, MOUSE, Point, Polygon, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode } from "pytha";
import { STRUCTURE_CTRL_TYPE } from "../enums/structure-control-type";
import { Vector3 } from "three";
import { SColumnT } from "../models/SColumnT";
import { STRUCTURE_SIGNAL } from "../enums/structure-signal";


/**
 * 添加结构柱二级控制器
 * 指定起点和终点布置
 */
export class AddSColumnControl extends BasicControl {
    id = STRUCTURE_CTRL_TYPE.ADD_SCOLUMN;

    needInit: boolean;

    capturePoint: Vector3;
    polarPoint: Vector3;
    orthogonalPoint: Vector3;
    point_down: Vector3 = new Vector3();
    point_move: Point = new Point();
    bottomFace: Polygon;

    // 第一个点坐标
    firstPoint: Vector3 = new Vector3();
    // 第二个点坐标
    secondPoint: Vector3 = new Vector3();

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    helpColumn: SColumnT;

    layerUuid: string = "";
    ex: number = 0;
    ey: number = 0;
    angle: number;
    step: number = 1;
    iBiasLength: number = 0;  //i节点退让
    jBiasLength: number = 0;  //j节点退让
    private boundInitControl: Function;
    // 构件信息
    componentData: any;

    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        this.needInit = true;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();

        this.editor = editor;
        this.listeners = listeners;

        this.editor.clampControl.enabled = false;
        this.editor.clampControl.hidden = true;

        // 保存绑定的函数引用
        this.boundInitControl = this.initControl.bind(this);
        this.listeners.getSignal(STRUCTURE_SIGNAL.initSColumnControl).add(this.boundInitControl);

        console.log('AddSColumnControl added')
    }

    dispose(): void {

        this.listeners.getSignal(STRUCTURE_SIGNAL.initSColumnControl).remove(this.boundInitControl);

        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);


        this.editor.selectControl.setEnabled(true);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;

        if (this.helpColumn && this.helpColumn.viewObj && this.helpColumn.viewObj.parent) {
            this.helpColumn.viewObj.parent?.remove(this.helpColumn.viewObj);
        }

        console.log('AddSColumnControl removed')
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }


    initControl(data: any): void {
        this.componentData = data;
        this.onAssembleSColumn(data, data.bottomFace, data.ex, data.ey, data.angle, data.layerUuid)
    }

    onAssembleSColumn = (data: any, bottomFace: Polygon, ex: number, ey: number, angle: number, layerUuid: string) => {
        let initData = {
            materialUuid: data.materialUuid,
            sectionUuid: data.sectionUuid,
            bottomFace: bottomFace,
            ex: ex,
            ey: ey,
            angle: angle,
            layerUuid: layerUuid
        }
        this.listeners.signals.setInitControlData.dispatch(initData);

        if (this.helpColumn && this.helpColumn.viewObj && this.helpColumn.viewObj.parent) {
            this.helpColumn.viewObj.parent?.remove(this.helpColumn.viewObj);
        }
        this.iBiasLength = this.componentData.iBiasLength;
        this.jBiasLength = this.componentData.jBiasLength;
        this.bottomFace = bottomFace;
        this.ex = ex;
        this.ey = ey;
        this.angle = angle;
        this.step = 1;
        this.layerUuid = layerUuid
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        this.listeners.signals.needCapture.dispatch();
        this.listeners.signals.onPointCapture.add(this.onPointCapture);
        this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        this.listeners.signals.emptySelectedEntities.dispatch();
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
        this.editor.selectControl.setEnabled(false);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.listeners.signals.sendToDymodeTip.dispatch('指定第一个点:');
        this.extraContext.stmodeContext.setStmode('指定第一个点', []);
    }


    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT) {
            this.extraContext.listeners.signals.onOpeCommandControlEnd.dispatch();
        }
        if (event.button !== MOUSE.LEFT) return;
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.step == 1) {
            // 获得了第一个点坐标
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.firstPoint = this.point_down.clone();
            this.firstStep();
        } else if (this.step == 2) {
            // 获得了第二个点坐标
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.secondPoint = this.point_down.clone();
            this.secondStep();
        }
    }

    private _dir = new Vector3();
    onPointerMove = (event) => {
        if (this.step != 2) return;
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        }
        if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        // 改变轴向和轴向长度
        this._dir.set(this.point_move.x - this.firstPoint.x, this.point_move.y - this.firstPoint.y, this.point_move.z - this.firstPoint.z);
        let normalRotateAxis = this.helpColumn._geo.direction.clone().cross(this._dir).normalize();
        let normalDeltaAngle = this.helpColumn._geo.direction.angleTo(this._dir);
        // 底面旋转至相应的角度
        if (normalRotateAxis.length() > 1e-6) {
            this.helpColumn._geo.rotate(this.firstPoint as Point, normalRotateAxis, normalDeltaAngle);
        }
        this.helpColumn._geo.height = this._dir.length();
        // let newAxisX = this.context.ucsContext.getAxisList().axisX.clone().projectOnPlane(this._dir);
        // 1轴（ij轴）× (0,0,1)
        let newAxisX = this._dir.clone().cross(this._worldZ).normalize();
        let axisXRotateAxis = this.helpColumn._geo.bottomFace.axisX.clone().cross(newAxisX).normalize();
        let axisXDeltaAngle = this.helpColumn._geo.bottomFace.axisX.angleTo(newAxisX);
        if (Math.abs(this.helpColumn._geo.bottomFace.axisX.dot(newAxisX) + 1) < 1e-6) {
            axisXRotateAxis = this._dir.clone().normalize();
        }
        if (axisXRotateAxis.length() > 1e-6) {
            // 绕axisX × axisX2，旋转axisX-axisX2角度，即旋转到当前ucs的X轴投影到截面的axisX方向
            this.helpColumn._geo.rotate(this.firstPoint as Point, axisXRotateAxis, axisXDeltaAngle);
        }
        this._dirNormalize.copy(this._dir).normalize();
        if (this._dirNormalize.length() > 1e-6) {
            this.helpColumn._geo.rotate(new Point().copy(this.firstPoint), this._dirNormalize, this.angle || 0);
        }
        this.helpColumn.updateViewGeometry();
        this.listeners.signals.needRender.dispatch();
    }

    firstStep = () => {
        this.listeners.signals.needCapture.dispatch(this.firstPoint);
        this.listeners.signals.orthogonal.dispatch(this.firstPoint);
        this.listeners.signals.polarTracking.dispatch(this.firstPoint);
        // 构造临时拉伸体
        this.generateSolid(this.context.ucsContext.getAxisList().axisZ, this.firstPoint, 1000);
        // 发送指定第二个点的信号
        this.listeners.signals.sendToDymodeTip.dispatch('指定第二个点:');
        this.extraContext.stmodeContext.setStmode('指定第二个点', []);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.firstPoint);
        this.step++;
        this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    }

    private _worldZ = new Vector3(0, 0, 1)
    private _dirNormalize = new Vector3();
    secondStep = () => {
        // 改变轴向和轴向长度
        this._dir.set(this.secondPoint.x - this.firstPoint.x, this.secondPoint.y - this.firstPoint.y, this.secondPoint.z - this.firstPoint.z);
        let normalRotateAxis = this.helpColumn._geo.direction.clone().cross(this._dir).normalize();
        let normalDeltaAngle = this.helpColumn._geo.direction.angleTo(this._dir);
        // 底面旋转至相应的角度
        if (normalRotateAxis.length() > 1e-6) {
            this.helpColumn._geo.rotate(this.firstPoint as Point, normalRotateAxis, normalDeltaAngle);
        }
        this.helpColumn._geo.height = this._dir.length();
        // let newAxisX = this.context.ucsContext.getAxisList().axisX.clone().projectOnPlane(this._dir);
        // 1轴（ij轴）× (0,0,1)
        let newAxisX = this._dir.clone().cross(this._worldZ).normalize();
        let axisXRotateAxis = this.helpColumn._geo.bottomFace.axisX.clone().cross(newAxisX).normalize();
        let axisXDeltaAngle = this.helpColumn._geo.bottomFace.axisX.angleTo(newAxisX);
        if (Math.abs(this.helpColumn._geo.bottomFace.axisX.dot(newAxisX) + 1) < 1e-6) {
            axisXRotateAxis = this._dir.clone().normalize();
        }
        if (axisXRotateAxis.length() > 1e-6) {
            // 绕axisX × axisX2，旋转axisX-axisX2角度，即旋转到当前ucs的X轴投影到截面的axisX方向
            this.helpColumn._geo.rotate(this.firstPoint as Point, axisXRotateAxis, axisXDeltaAngle);
        }
        this._dirNormalize.copy(this._dir).normalize();
        if (this._dirNormalize.length() > 1e-6) {
            this.helpColumn._geo.rotate(new Point().copy(this.firstPoint), this._dirNormalize, this.angle || 0);
        }
        this.helpColumn.updateViewGeometry();
        this.listeners.signals.needRender.dispatch();
        this.insertSColumn();
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.noNeedCapture.dispatch();
        this.onAssembleSColumn(this.componentData, this.bottomFace, this.ex, this.ey, this.angle, this.layerUuid);
    }

    generateSolid = (normal: Vector3 = null, basePoint: Vector3, height: number = 0) => {
        // 体的轴线方向
        let lineDir = normal.clone().normalize();
        // 获得截面bottomFace用的是世界坐标系
        let worldOrigin = new Point(0, 0, 0);
        let worldZ = new Vector3(0, 0, 1);
        let bottomFace = this.bottomFace.clone();

        // 体轴心的偏移
        let offsetDir = new Vector3(this.ex || 0, this.ey || 0, 0);
        let offsetDirNor = offsetDir.clone().normalize();
        // 将体的轴心偏离轴线
        bottomFace.move(offsetDirNor, offsetDir.length());


        let normalRotateAxis = bottomFace.normal.clone().cross(lineDir).normalize();
        let normalDeltaAngle = bottomFace.normal.angleTo(lineDir);
        // 底面旋转至相应的角度
        if (normalRotateAxis.length() > 1e-6) {
            bottomFace.rotate(worldOrigin, normalRotateAxis, normalDeltaAngle);
        }
        // 将截面平移至相应位置
        bottomFace.move(basePoint.clone().normalize(), basePoint.length());
        this.helpColumn = new SColumnT(bottomFace, lineDir, height);
        this.helpColumn._geo.generateBottomMiddlePoints();

        // let newAxisX = this.context.ucsContext.getAxisList().axisX.clone().projectOnPlane(lineDir);
        // 1轴（ij轴）× (0,0,1)
        let newAxisX = lineDir.clone().cross(worldZ).normalize();
        let axisXRotateAxis = bottomFace.axisX.clone().cross(newAxisX).normalize();
        let axisXDeltaAngle = bottomFace.axisX.angleTo(newAxisX);
        if (Math.abs(bottomFace.axisX.dot(newAxisX) + 1) < 1e-6) {
            axisXRotateAxis = lineDir.clone();
        }
        if (axisXRotateAxis.length() > 1e-6) {
            // 绕bottomFace.axisX × axisX2，旋转bottomFace.axisX-axisX2角度，即旋转到当前ucs的X轴投影到截面的axisX方向
            this.helpColumn.rotate(basePoint as Point, axisXRotateAxis, axisXDeltaAngle);
        }
        this.helpColumn.rotate(basePoint as Point, lineDir, this.angle || 0);
        this.extraContext.sceneContext.scene.add(this.helpColumn.render());
        this.listeners.signals.needRender.dispatch();
    }

    // 添加斜柱
    insertSColumn = () => {
        let column: SColumnT = this.helpColumn.clone();
        column.layerUuid = this.layerUuid;
        column.isColorByLayer = true;
        column.materialUuid = this.componentData.materialUuid;
        column.sectionUuid = this.componentData.sectionUuid;
        if(Math.abs(this.iBiasLength) > 1e-6 || Math.abs(this.jBiasLength) > 1e-6 ){
            //退让
            column._geo.bottomFace.move(column._geo.direction.clone(), this.iBiasLength);
            column._geo.height = column._geo.height - this.iBiasLength - this.jBiasLength;
            column._geo.generateBottomMiddlePoints();
        }
        this.editor.history.execute(new AddPrimitivesCommand([column]));
    }

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            if (this.step == 1) {
                // 获得了第一个点
                this.firstPoint.set(value.x, value.y, value.z);
                this.firstStep();
            } else if (this.step == 2) {
                // 获得了第二个点
                this.secondPoint.set(value.x, value.y, value.z);
                this.secondStep();
            }
        } else if (value.mode === DYMODE_MODE.DIST) {
            if (this.step == 2) {
                this._dir = this.point_move.clone().sub(this.firstPoint).normalize();
                let end = this._dir.multiplyScalar(value.dist).add(this.firstPoint);
                this.secondPoint.set(end.x, end.y, end.z);
                this.secondStep();
            }
        }
    }
}

